// 前端 Agent 循环：驱动 DeepSeek（经 /api/ai/llm 代理）进行工具编排。
// 最大迭代 10 + 重复工具检测 3 次退出；工具执行走 executor（window.api）；全过程通过回调驱动 UI。

import { callWithTools, callStream } from './llmClient';
import type { LLMUsageMeta } from './llmClient';
import { executeXiaotianshuTool, XIAOTIANSHU_TOOLS } from './executor';
import { debugLogger } from '../utils/debugLogger';
import type { AIMessage, AgentContext } from '../types';

const MAX_AGENT_ITERATIONS = 10;
const MAX_REPEATED_TOOL_CALLS = 3;

export interface RunAgentCallbacks {
  onToolCall?: (toolName: string) => void;
  onToolResult?: (toolName: string, success: boolean, detail?: string) => void;
  onContent?: (content: string) => void;
  onThinking?: (thinking: string) => void;
  onAgentMessage?: (text: string) => void | Promise<void>;
  onAgentThinking?: (thinking: string, iteration: number) => void | Promise<void>;
  /** 工具调用参数（原始 JSON 文本，供卡片渲染；harness 引擎提供） */
  onToolCallDetail?: (toolName: string, argsText: string) => void;
  /** 工具结果全文（供卡片渲染；harness 引擎提供） */
  onToolResultDetail?: (toolName: string, success: boolean, resultText: string) => void;
  /** 一段思考结束（harness 引擎提供）：用于闭合当前"深度思考中"标签、下一段思考新开标签 */
  onThinkingPhaseEnd?: () => void;
  /**
   * 事件流**已连上**（harness 引擎提供）。
   *
   * 附着模式（`attachOnly`）下没有"回合结束"可等 —— 服务端那个回合可能正停在审批上
   * 等用户拍板，`runHarnessAgent` 的 Promise 要等用户答复后才 settle。
   * 所以"这次附着到底成没成"只能靠这个信号判断：调用方据此实施重试/报错。
   */
  onStreamOpen?: () => void;
}

export interface RunAgentParams {
  model: string;
  /** 供应商（deepseek / openrouter）；缺省用默认供应商 */
  provider?: string;
  /** 模型推理力度（模型速率）：low / high / max；缺省用模型默认 */
  reasoningEffort?: string;
  messages: AIMessage[];
  context: AgentContext;
  signal: AbortSignal;
  /** 用量归因（会话 ID / 作品 ID），透传给服务端记录 token 消耗 */
  usageMeta?: LLMUsageMeta;
  callbacks?: RunAgentCallbacks;
  /**
   * **附着模式**（仅 Harness 引擎）：不发新消息，只接到已存在的服务端会话上，
   * 把它还挂着的审批/提问重新接回来（服务端在订阅时会补发）。
   *
   * 用于页面刷新/重连之后把"停在审批上的回合"接回界面 —— 审批不设超时，所以服务端还在等，
   * 但旧事件早已发过、不会重放，不重连就永远看不见也答不了那张卡。
   */
  attachOnly?: boolean;
}

export interface RunAgentResult {
  content: string;
  thinking: string;
  toolMessages: AIMessage[];
}

function toolResultSummary(toolResult: unknown): string {
  try {
    const r = toolResult as Record<string, unknown>;
    if (Array.isArray(toolResult)) return `返回 ${(toolResult as unknown[]).length} 条记录`;
    if (r?.message) return String(r.message);
    if (r?.currentChapter) return `当前章节: ${(r.currentChapter as Record<string, unknown>)?.title ?? ''}`;
    if (r?.data && Array.isArray(r.data)) return `返回 ${(r.data as unknown[]).length} 条数据`;
    if (r?.outline) return `大纲 ${String(r.outline).length} 字符`;
    if (r?.error) return String(r.error);
    return JSON.stringify(toolResult).slice(0, 80);
  } catch {
    return '';
  }
}

async function runFinalStream(
  model: string,
  currentMessages: AIMessage[],
  initialMessageCount: number,
  signal: AbortSignal,
  usageMeta: LLMUsageMeta | undefined,
  provider: string | undefined,
  cb: RunAgentCallbacks,
  reasoningEffort?: string,
): Promise<RunAgentResult> {
  const { content, thinking } = await callStream(model, currentMessages, signal, cb.onThinking, cb.onContent, usageMeta, provider, reasoningEffort);
  const toolMessages = currentMessages.slice(initialMessageCount);
  return { content, thinking, toolMessages };
}

export async function runAgent(params: RunAgentParams): Promise<RunAgentResult> {
  const { model, provider, reasoningEffort, messages, context, signal, usageMeta, callbacks = {} } = params;

  let iterations = 0;
  const currentMessages = [...messages];

  let consecutiveRepeatCount = 0;
  let lastToolCallsSignature = '';
  const agentStart = Date.now();

  debugLogger.system('AGENT_RUN_START', { model, messageCount: messages.length, chapterId: context.chapterId, bookId: context.bookId });

  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (signal.aborted) throw new Error('请求已取消');

    if (iterations >= MAX_AGENT_ITERATIONS) {
      debugLogger.systemWarn('AGENT_MAX_ITERATIONS', { iterations, elapsed: Date.now() - agentStart });
      return runFinalStream(model, currentMessages, messages.length, signal, usageMeta, provider, callbacks, reasoningEffort);
    }

    const response = await callWithTools(model, currentMessages, XIAOTIANSHU_TOOLS, signal, usageMeta, provider, reasoningEffort);

    if (response.tool_calls?.length) {
      if (response.thinking) {
        await callbacks.onAgentThinking?.(response.thinking, iterations + 1);
      }
      const dialogueText = (response.content ?? '').trim();
      if (dialogueText) {
        await callbacks.onAgentMessage?.(dialogueText);
      }

      const currentSignature = response.tool_calls.map((tc) => `${tc.function.name}:${tc.function.arguments}`).sort().join('|');
      if (currentSignature === lastToolCallsSignature) {
        consecutiveRepeatCount++;
        if (consecutiveRepeatCount >= MAX_REPEATED_TOOL_CALLS) {
          debugLogger.systemWarn('AGENT_REPEATED_TOOL_EXIT', { iterations, elapsed: Date.now() - agentStart });
          return runFinalStream(model, currentMessages, messages.length, signal, usageMeta, provider, callbacks, reasoningEffort);
        }
      } else {
        consecutiveRepeatCount = 0;
        lastToolCallsSignature = currentSignature;
      }

      const assistantMsg: AIMessage = { role: 'assistant', content: response.content ?? '', tool_calls: response.tool_calls, thinking: response.thinking };
      currentMessages.push(assistantMsg);

      for (const toolCall of response.tool_calls) {
        const toolName = toolCall.function.name;
        callbacks.onToolCall?.(toolName);

        let toolResult: unknown;
        try {
          const args = JSON.parse(toolCall.function.arguments || '{}') as Record<string, unknown>;
          toolResult = await executeXiaotianshuTool(toolName, args, context);
          const resultObj = toolResult as Record<string, unknown> | undefined;
          const hasError = resultObj?.error || resultObj?.success === false;
          const summary = toolResultSummary(toolResult);
          callbacks.onToolResult?.(toolName, !hasError, summary);
        } catch (e) {
          if (signal.aborted) throw new Error('请求已取消');
          const errMsg = e instanceof Error ? e.message : '工具执行失败';
          toolResult = { error: errMsg };
          callbacks.onToolResult?.(toolName, false, errMsg);
        }

        const resultJson = JSON.stringify(toolResult);
        currentMessages.push({ role: 'tool', content: resultJson, tool_call_id: toolCall.id });
      }

      iterations++;
    } else {
      debugLogger.system('AGENT_STOP_EXIT', { iterations, elapsed: Date.now() - agentStart, messageCount: currentMessages.length });
      return runFinalStream(model, currentMessages, messages.length, signal, usageMeta, provider, callbacks);
    }
  }
}
